import { onMounted, onBeforeUnmount, watch, type Ref } from 'vue'
import { useAnalytics, getProductOpenContext } from '@shared/lib'
import type { Product, ComboProduct, ProductVariant } from '~/entities/product'

/**
 * Воронка карточки товара: открытие → выбор размера → модификаторы → корзина → закрытие.
 *
 * Логика вынесена из компонента намеренно: карточка и так на 700 строк, а события
 * тут связаны между собой (dwell-время и «была ли вообще активность» считаются
 * от момента открытия). Компонент дёргает только `trackAdded` / `trackFailed` —
 * остальное composable отслеживает сам.
 */
export interface UseProductModalAnalyticsOptions {
  currentVariant: Ref<ProductVariant>
  selectedAddons: Ref<number[]>
  selectedExcludes: Ref<number[]>
  /**
   * Продукт целиком. Опционален: у легаси-карточки отдельной ссылки на него нет,
   * а для событий хватает текущего варианта.
   */
  product?: Ref<Product | ComboProduct | null>
  /** Радио — у напитков, табы — у размеров. Различие видно в отчёте по шагу выбора. */
  useRadioButtons?: Ref<boolean>
}

/**
 * Эстафета одного логического открытия через ремаунт. DynamicModal перерисовывает
 * содержимое сменой :key (например, когда карточка просит другой размер окна), и без
 * эстафеты каждый ремаунт давал пару лишних событий: close с dwell≈0 и второй open.
 * Если новая карточка того же товара монтируется в это окно — открытие то же самое.
 */
const REMOUNT_HANDOFF_MS = 250

interface PendingClose {
  productId: number
  openedAt: number
  hadInteraction: boolean
  addedToCart: boolean
  /**
   * Успел ли предыдущий инстанс сообщить об открытии.
   *
   * Ремаунт случается в том же тике, и Vue не вызывает `onMounted` у компонента,
   * который к моменту post-flush уже размонтирован, — а `onBeforeUnmount` вызывает.
   * Без этого флага эстафета считала открытие отправленным, и первый шаг воронки
   * не уходил вообще: в отчёте была пустая корзина при живых кликах.
   */
  openEmitted: boolean
  timer: ReturnType<typeof setTimeout>
}

let pendingClose: PendingClose | null = null

export function useProductModalAnalytics(options: UseProductModalAnalyticsOptions) {
  const { product, currentVariant, selectedAddons, selectedExcludes, useRadioButtons } = options

  const { track } = useAnalytics()

  let openedAt = Date.now()
  // Контекст снимаем один раз при создании: пока карточка открыта, он не меняется,
  // а следующее открытие его перезапишет.
  const { source, categoryId } = getProductOpenContext()

  /** Отличает «посмотрел и закрыл» от «повертел варианты и всё равно закрыл». */
  let hadInteraction = false
  let addedToCart = false
  let openEmitted = false

  const productId = (): number => Number(currentVariant.value?.id ?? (product?.value as any)?.id ?? 0)

  const findAddon = (id: number) => currentVariant.value?.addons?.find(a => a.id === id)
  const findExclude = (id: number) => currentVariant.value?.excludes?.find(e => e.id === id)

  /**
   * Списки модификаторов сравниваем сами: чекбоксы правят массив напрямую (`v-model`),
   * отдельного события «переключили добавку» в компоненте нет.
   */
  const watchSelection = (
    selection: Ref<number[]>,
    kind: 'addon' | 'exclude',
  ): void => {
    // Свой снимок предыдущего состояния, а не аргумент `previous` из watch: чекбоксы
    // мутируют тот же массив через push/splice, и Vue отдаёт в оба аргумента ОДНУ
    // ссылку — сравнение с ним всегда даёт пустой diff, и шаг воронки исчезает.
    let previousIds = [...selection.value]

    watch(
      selection,
      (next) => {
        const added = next.filter(id => !previousIds.includes(id))
        const removed = previousIds.filter(id => !next.includes(id))
        previousIds = [...next]

        if (added.length === 0 && removed.length === 0) return
        hadInteraction = true

        for (const id of added) {
          const item = kind === 'addon' ? findAddon(id) : findExclude(id)
          track('product_addon_toggle', {
            productId: productId(),
            addonId: id,
            addonName: item?.name,
            action: 'add',
            kind,
            price: kind === 'addon' ? (item as { price?: number } | undefined)?.price : undefined,
          })
        }

        for (const id of removed) {
          const item = kind === 'addon' ? findAddon(id) : findExclude(id)
          track('product_addon_toggle', {
            productId: productId(),
            addonId: id,
            addonName: item?.name,
            action: 'remove',
            kind,
            price: kind === 'addon' ? (item as { price?: number } | undefined)?.price : undefined,
          })
        }
      },
      // Массив мутируется через splice/push — без deep изменения не видны.
      { deep: true },
    )
  }

  const emitOpen = (): void => {
    if (openEmitted) return
    openEmitted = true

    track('product_modal_open', {
      productId: productId(),
      productName: currentVariant.value?.name ?? (product?.value as any)?.name,
      categoryId,
      price: currentVariant.value?.price,
      source,
    })
  }

  onMounted(() => {
    // Ремаунт того же товара — продолжение прежнего открытия: перенимаем его состояние
    // (момент открытия, была ли активность, ушло ли уже открытие).
    if (pendingClose && pendingClose.productId === productId()) {
      clearTimeout(pendingClose.timer)
      openedAt = pendingClose.openedAt
      hadInteraction = pendingClose.hadInteraction
      addedToCart = pendingClose.addedToCart
      openEmitted = pendingClose.openEmitted
      pendingClose = null
    }

    // Открытие отправляет тот инстанс, который до него дожил: предыдущий мог быть
    // размонтирован раньше, чем Vue дошёл до его onMounted.
    emitOpen()
  })

  // Первое срабатывание пропускаем: вариант выставляется автоматически при открытии,
  // это не выбор пользователя.
  watch(
    () => currentVariant.value?.id,
    (newId, oldId) => {
      if (!newId || !oldId || newId === oldId) return
      hadInteraction = true

      track('product_variant_select', {
        productId: Number(newId),
        variantId: Number(newId),
        variantName: currentVariant.value?.name,
        control: useRadioButtons?.value ? 'radio' : 'tab',
      })
    },
  )

  watchSelection(selectedAddons, 'addon')
  watchSelection(selectedExcludes, 'exclude')

  const emitClose = (state: Omit<PendingClose, 'timer'>): void => {
    // Закрытие без открытия — не событие воронки: так выглядит инстанс, снятый
    // ремаунтом раньше, чем он успел о себе сообщить.
    if (!state.openEmitted) return

    track('product_modal_close', {
      productId: state.productId,
      dwellMs: Date.now() - state.openedAt,
      hadInteraction: state.hadInteraction,
      addedToCart: state.addedToCart,
    })
  }

  onBeforeUnmount(() => {
    // Закрытие откладываем на мгновение: если это ремаунт, новая карточка успеет
    // подхватить состояние и событие не уйдёт. Настоящее закрытие уедет по таймеру.
    // Чужое отложенное закрытие (другой товар) провожаем сразу — оно уже настоящее.
    if (pendingClose) {
      clearTimeout(pendingClose.timer)
      emitClose(pendingClose)
      pendingClose = null
    }

    const state = {
      productId: productId(),
      openedAt,
      hadInteraction,
      addedToCart,
      openEmitted,
    }

    const timer = setTimeout(() => {
      if (pendingClose?.timer !== timer) return
      pendingClose = null
      emitClose(state)
    }, REMOUNT_HANDOFF_MS)

    pendingClose = { ...state, timer }
  })

  /** Вызывать ПОСЛЕ успешного добавления: событие означает подтверждённую позицию в корзине. */
  const trackAdded = (payload: { quantity: number; total: number }): void => {
    hadInteraction = true
    addedToCart = true

    track('add_to_cart', {
      productId: productId(),
      productName: currentVariant.value?.name,
      categoryId,
      quantity: payload.quantity,
      total: payload.total,
      price: currentVariant.value?.price,
      variantId: Number(currentVariant.value?.id) || undefined,
      addonIds: [...selectedAddons.value],
      source,
    })
  }

  const trackFailed = (reason: 'unavailable' | 'branch_mismatch' | 'validation' | 'network' | 'unknown', message?: string): void => {
    hadInteraction = true

    track('add_to_cart_failed', {
      productId: productId(),
      reason,
      message,
      source,
    })
  }

  return { trackAdded, trackFailed }
}
